// Application client form state. Used to keep form values in context because resizing the client might trigger

import type { InputValidationState } from '@landsbankinn/ui-kit';
import { current } from 'immer';
import type { AllowedLoginType } from 'src/state';
import type { ImmerReducer } from 'use-immer';

type FormStateTypeValue = {
  value: string;
  validationState: InputValidationState;
};

// a layout swap between mobile and desktop and clear values from the user.
type ApplicationClientFormStateType = {
  username?: FormStateTypeValue;
  password?: FormStateTypeValue;
  phonenumber?: FormStateTypeValue;
  authenticationNumber?: FormStateTypeValue;
  kennitala?: FormStateTypeValue;
  code?: FormStateTypeValue;
  xsrf?: string;
};

// Application client state (if required)
export type ApplicationClientStateType = {
  currentLoginType: AllowedLoginType | null;
  errors?: string[];
  errorStatus?: ErrorStatusType;
  /**
   * Denied login attempts for certain clients (e.g. Landsbanki App) requires
   * end-user to cancel the current login attempt instead of retrying it.
   * Default action for undefined value is always "Retry".
   */
  errorRetryAction?: ErrorRetryActionType;
  form?: ApplicationClientFormStateType;
  isSubmitting: boolean;
  isSubmittingCancel: boolean;
  showMarketingContent: boolean;
  isSubmittingTwoStepLogin: boolean;
  isLandsbankinnApp: boolean;
};

type ErrorStatusType = 'Bad Request' | 'Forbidden' | undefined;
type ErrorRetryActionType = 'Retry' | 'Cancel' | undefined;

type ApplicationFormValueType = (
  | {
      input:
        | 'username'
        | 'password'
        | 'phonenumber'
        | 'authenticationNumber'
        | 'kennitala'
        | 'code';
      validationState: InputValidationState;
    }
  | {
      input: 'xsrf';
    }
) & { value: string };

/**
 * All action types that are dispatched in the application client.
 */
export type ApplicationClientActionType =
  | { type: 'setLoginType'; payload: { loginType: AllowedLoginType | null } }
  | { type: 'setAnimating'; payload: boolean }
  | {
      type: 'setFormValues';
      payload: {
        fields: ApplicationFormValueType[];
        loginType: AllowedLoginType;
      };
    }
  | { type: 'clearFormValues' }
  | { type: 'clearServerErrorState' }
  | {
      type: 'setIsSubmitting';
      payload?: { isSubmitting?: boolean; isSubmittingCancel?: boolean };
    }
  | { type: 'showMarketingContent' }
  | { type: 'setIsSubmittingTwoStepLogin'; payload?: { isSubmitting: boolean } }
  | {
      type: 'setLoginErrorState';
      payload?: {
        errorMessage: string;
        status?: ErrorStatusType;
        retryAction?: ErrorRetryActionType;
      };
    };

export interface IClientStateReducerProvider {
  /**
   * A reducer function for the application that updates it's client state given an action.
   * @param draft A proxy to the original base state that can be safely mutated
   * @param action The ApplicationActionType action dispatched
   */
  reducer: ImmerReducer<
    ApplicationClientStateType,
    ApplicationClientActionType
  >;
}

export class ClientStateReducerProvider implements IClientStateReducerProvider {
  private state: ApplicationClientStateType | undefined;

  constructor(initState: ApplicationClientStateType | undefined = undefined) {
    this.state = initState;
    this.reducer = this.reducer.bind(this);
  }

  reducer(
    draft: ApplicationClientStateType,
    action: ApplicationClientActionType,
  ): void {
    switch (action.type) {
      case 'setLoginType': {
        draft.currentLoginType = action.payload.loginType;
        break;
      }

      case 'setFormValues': {
        if (!draft.form) draft.form = {};
        action.payload.fields.forEach((field) => {
          if (draft.form) {
            if (field.input === 'xsrf') {
              draft.form[field.input] = field.value;
            } else {
              draft.form[field.input] = {
                value: field.value,
                validationState: field.validationState,
              };
            }
          }
        });
        draft.currentLoginType = action.payload.loginType;
        break;
      }

      case 'clearFormValues': {
        draft.form = this.state?.form;
        draft.isSubmitting = false;
        break;
      }

      case 'clearServerErrorState': {
        draft.errors = undefined;
        draft.errorStatus = undefined;
        draft.errorRetryAction = undefined;
        break;
      }

      case 'setIsSubmitting':
        draft.isSubmitting =
          action.payload === undefined
            ? true
            : (action.payload.isSubmitting ??
              !action.payload.isSubmittingCancel);
        draft.isSubmittingCancel =
          action.payload === undefined
            ? false
            : (action.payload.isSubmittingCancel ?? false);
        break;

      case 'showMarketingContent':
        draft.showMarketingContent = true;
        break;

      case 'setIsSubmittingTwoStepLogin':
        draft.isSubmittingTwoStepLogin =
          action.payload !== undefined ? action.payload.isSubmitting : true;
        break;

      case 'setLoginErrorState':
        draft.isSubmitting = false;
        draft.isSubmittingCancel = false;
        draft.isSubmittingTwoStepLogin = false;
        draft.errors =
          action.payload === undefined ? [] : [action.payload.errorMessage];
        draft.errorStatus = action.payload?.status;
        draft.errorRetryAction = action.payload?.retryAction;
        break;
    }

    this.state = current(draft);
  }
}
export const ClientStateReducerProviderToken = Symbol(
  ClientStateReducerProvider.name,
);
